/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Snowflake RBAC", description = "Learn how Hightouch honors your Snowflake data governance in in-app previews by mapping user groups to Snowflake roles, so masking policies, row access policies, and role grants apply as they do in Snowflake.", lastUpdatedDate = new Date(1782777600000), keywords = "snowflake rbac, role-based access control, snowflake roles, column masking, masking policy, row access policy, data governance, preview governance, role mapping, current_role";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Snowflake RBAC",
        description: "Learn how Hightouch honors your Snowflake data governance in in-app previews by mapping user groups to Snowflake roles, so masking policies, row access policies, and role grants apply as they do in Snowflake.",
        lastUpdatedDate: "Tue Jun 30 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "snowflake-rbac",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Scope",
          "depth": 0,
          "id": "scope"
        }, {
          "value": "How it works",
          "depth": 0,
          "id": "how-it-works"
        }, {
          "value": "Set up Snowflake",
          "depth": 0,
          "id": "set-up-snowflake"
        }, {
          "value": "Set up role mappings in Hightouch",
          "depth": 0,
          "id": "set-up-role-mappings-in-hightouch"
        }, {
          "value": "Keeping roles in sync",
          "depth": 0,
          "id": "keeping-roles-in-sync"
        }, {
          "value": "Limitations",
          "depth": 0,
          "id": "limitations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      hr: "hr",
      ul: "ul",
      li: "li",
      em: "em",
      code: "code",
      pre: "pre",
      span: "span",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Snowflake RBAC lets Hightouch honor your Snowflake data governance when it runs in-app preview queries. When it's enabled, Hightouch maps your user groups to Snowflake roles and runs each user's previews under their resolved role, so Snowflake's masking policies, row access policies, and role grants apply exactly as they do in Snowflake itself."
      }), "\n", _jsxs(_components.p, {
        children: ["Without it, every Hightouch warehouse query runs under the single ", _jsx(_components.a, {
          href: "/sources/snowflake#separate-hightouch-from-human-users",
          children: "service account"
        }), " configured on the source. With it, a marketer browsing an audience preview sees the same masked columns and filtered rows that Snowflake would show that marketer directly."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This applies to ", _jsx(_components.strong, {
            children: "in-app previews"
          }), " only. Syncs, materializations, and other non-preview operations continue to run under the source's service account. See ", _jsx(_components.a, {
            href: "#scope",
            children: "Scope"
          }), " below."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "scope",
        children: "Scope"
      }), "\n", _jsx(_components.p, {
        children: "Snowflake RBAC governs the queries Hightouch runs on your behalf to show you data in the app:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Model and rETL previews"
        }), "\n", _jsx(_components.li, {
          children: "Audience previews and the audience member browser"
        }), "\n", _jsx(_components.li, {
          children: "Trait previews"
        }), "\n", _jsx(_components.li, {
          children: "Journey-condition previews"
        }), "\n", _jsx(_components.li, {
          children: "Table browsing and ad-hoc counts"
        }), "\n", _jsx(_components.li, {
          children: "Raw SQL previews"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The following keep running under the service account, by design:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync runs and materializations."
          }), " These are pipeline operations, not per-user views of data."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience size estimates and live counts."
          }), " These are served from a shared cache, so they are not scoped to an individual user's role."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Charts."
          }), " Charts read pre-computed aggregates produced by syncs running under the service account. To keep governed values out of charts, exclude them from the models that feed your charts."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-it-works",
        children: "How it works"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Per source."
          }), " RBAC is configured on each Snowflake source independently."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Role mappings."
          }), " You map a Hightouch user group to a Snowflake role. Each mapping has a priority."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Default role."
          }), " The role used for previews when no mapping matches the user. Saving a default role is what enables RBAC on the source."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Resolution."
          }), " For a given user, Hightouch uses the highest-priority mapping that matches any of the user's groups. If no mapping matches, it uses the default role."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Role chip."
          }), " Governed previews display a chip showing which role the query ran as and why (for example, ", _jsxs(_components.em, {
            children: ["Ran as ", _jsx(_components.code, {
              children: "MARKETER"
            }), " · via group mapping"]
          }), "). The chip reflects the role verified at query time."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A workspace with RBAC available but no default role saved behaves exactly as it does today: all queries run under the service account."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "set-up-snowflake",
        children: "Set up Snowflake"
      }), "\n", _jsx(_components.p, {
        children: "Create the roles you want to map and grant them to the Snowflake user your Hightouch source authenticates as. A role must be granted to that user, or Hightouch cannot run queries under it."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["USE ROLE ACCOUNTADMIN;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Roles you will map to Hightouch user groups"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ROLE IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " ht_analyst;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ROLE IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " ht_marketer;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Each mapped role needs the usage and read it requires"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " WAREHOUSE ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "warehouse", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), "      ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE ht_analyst;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " WAREHOUSE ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "warehouse", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), "      ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE ht_marketer;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " DATABASE ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "database", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), "        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE ht_analyst;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " DATABASE ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "database", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), "        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE ht_marketer;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "database", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "schema", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE ht_analyst;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "database", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "schema", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE ht_marketer;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " TABLES ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " SCHEMA ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "database", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "schema", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE ht_analyst;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " TABLES ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " SCHEMA ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "database", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "schema", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE ht_marketer;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- The mapped roles must be granted to the source's Snowflake user"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ROLE ht_analyst  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "USER"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "hightouch_service_user", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ROLE ht_marketer ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "USER"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "hightouch_service_user", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ";\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Define governance against the role names with Snowflake's own ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/user-guide/security-column-intro",
          children: "masking policies"
        }), " and ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/user-guide/security-row-intro",
          children: "row access policies"
        }), ", keyed on ", _jsx(_components.code, {
          children: "CURRENT_ROLE()"
        }), ". For example, a masking policy that returns raw ", _jsx(_components.code, {
          children: "email"
        }), " only for ", _jsx(_components.code, {
          children: "HT_ANALYST"
        }), " and masks it otherwise."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "set-up-role-mappings-in-hightouch",
        children: "Set up role mappings in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open your Snowflake source and go to the ", _jsx(_components.strong, {
            children: "Role Mapping"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Refresh roles"
          }), ". Hightouch discovers the roles currently granted to the source's Snowflake user and records them."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set a ", _jsx(_components.strong, {
            children: "default role"
          }), ". Saving the default role enables RBAC for this source."]
        }), "\n", _jsxs(_components.li, {
          children: ["Add ", _jsx(_components.strong, {
            children: "mappings"
          }), " from user groups to roles, and drag to set their ", _jsx(_components.strong, {
            children: "priority"
          }), ". The highest-priority matching mapping wins."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "keeping-roles-in-sync",
        children: "Keeping roles in sync"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Refresh is manual."
          }), " Hightouch discovers roles when you click ", _jsx(_components.strong, {
            children: "Refresh roles"
          }), ". There is no background polling."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Revoked roles."
          }), " If a mapped role is later revoked from the source's Snowflake user, it is marked unavailable at the next refresh and skipped: resolution falls through to the next available mapping in priority order, then to the default role. It never widens to the service account."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "No usable role."
          }), " If none of a user's mapped roles is available and the default role is also unavailable, that user's previews are blocked until a role is restored or re-mapped, rather than falling back to the broader service account."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connection changes."
          }), " If the source's connection identity (account, login user, or authentication) changes, all previews on the source are blocked until you refresh roles under the new identity. This prevents serving data under a governance context that no longer matches the connection."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "limitations",
        children: "Limitations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Snowflake only."
          }), " Role-based preview governance is available for Snowflake sources. Other warehouses are not supported."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Previews only."
          }), " As described in ", _jsx(_components.a, {
            href: "#scope",
            children: "Scope"
          }), ", syncs, materializations, audience size, live counts, and charts run under the service account."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Group-based mapping."
          }), " Mappings are from Hightouch user groups to Snowflake roles. Per-user and identity-provider-driven mapping are not supported."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
